import { useState, useEffect } from 'react';
import { Save, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    // Contact
    phone: '',
    email: '',
    address: '',
    city: '',
    region: '',
    hours: '',
    
    // Tracking Pixels
    meta_pixel_id: '',
    google_analytics_id: '',
    google_tag_manager_id: '',
    tiktok_pixel_id: '',
    linkedin_pixel_id: '',
    
    // SEO
    meta_title: '',
    meta_description: '',
    
    // Email (SendGrid)
    sendgrid_api_key: '',
    notification_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  const getAuthHeader = () => {
    const password = localStorage.getItem('idmat_admin');
    return {
      headers: {
        'Authorization': 'Basic ' + btoa('admin:' + password)
      }
    };
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/admin/settings`, getAuthHeader());
        setSettings(prev => ({ ...prev, ...response.data }));
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, getAuthHeader());
      toast.success('Paramètres mis à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-[#F5C500] border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div data-testid="admin-settings">
        <div className="flex items-center justify-between mb-8">
          <h1 
            className="text-3xl font-bold text-[#1a1a1a]"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Paramètres du Site
          </h1>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#F5C500] text-[#1a1a1a] px-6 py-3 font-bold uppercase tracking-wide hover:bg-[#ddb200] transition-colors disabled:opacity-50"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            data-testid="save-settings-btn"
          >
            <Save size={18} />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>

        <div className="space-y-8">
          {/* Contact Info */}
          <div className="bg-white p-6 shadow-sm">
            <h2 
              className="text-xl font-bold text-[#1a1a1a] mb-6 border-b border-gray-100 pb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Informations de Contact
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Téléphone
                </label>
                <input
                  type="text"
                  value={settings.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  data-testid="input-phone"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  data-testid="input-email"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Adresse
                </label>
                <input
                  type="text"
                  value={settings.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Ville / Code postal
                </label>
                <input
                  type="text"
                  value={settings.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Région
                </label>
                <input
                  type="text"
                  value={settings.region}
                  onChange={(e) => handleChange('region', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Horaires d'ouverture
                </label>
                <input
                  type="text"
                  value={settings.hours}
                  onChange={(e) => handleChange('hours', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  placeholder="Lun - Ven : 07h00 - 16h00"
                />
              </div>
            </div>
          </div>

          {/* Tracking Pixels */}
          <div className="bg-white p-6 shadow-sm">
            <h2 
              className="text-xl font-bold text-[#1a1a1a] mb-6 border-b border-gray-100 pb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Pixels de Tracking
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Meta Pixel (Facebook) ID
                </label>
                <input
                  type="text"
                  value={settings.meta_pixel_id}
                  onChange={(e) => handleChange('meta_pixel_id', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  placeholder="Ex: 1234567890123456"
                  data-testid="input-meta-pixel"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Trouvez votre ID dans Meta Events Manager
                </p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Google Analytics ID (GA4)
                </label>
                <input
                  type="text"
                  value={settings.google_analytics_id}
                  onChange={(e) => handleChange('google_analytics_id', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  placeholder="Ex: G-XXXXXXXXXX"
                  data-testid="input-ga"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Format: G-XXXXXXXXXX
                </p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Google Tag Manager ID
                </label>
                <input
                  type="text"
                  value={settings.google_tag_manager_id}
                  onChange={(e) => handleChange('google_tag_manager_id', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  placeholder="Ex: GTM-XXXXXXX"
                  data-testid="input-gtm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Format: GTM-XXXXXXX
                </p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  TikTok Pixel ID
                </label>
                <input
                  type="text"
                  value={settings.tiktok_pixel_id}
                  onChange={(e) => handleChange('tiktok_pixel_id', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  placeholder="Ex: CXXXXXXXXXXXXXXXXX"
                  data-testid="input-tiktok"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  LinkedIn Insight Tag ID
                </label>
                <input
                  type="text"
                  value={settings.linkedin_pixel_id}
                  onChange={(e) => handleChange('linkedin_pixel_id', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  placeholder="Ex: 1234567"
                  data-testid="input-linkedin"
                />
              </div>
            </div>
          </div>

          {/* SEO */}
          <div className="bg-white p-6 shadow-sm">
            <h2 
              className="text-xl font-bold text-[#1a1a1a] mb-6 border-b border-gray-100 pb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              SEO (Référencement)
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Titre Meta (balise title)
                </label>
                <input
                  type="text"
                  value={settings.meta_title}
                  onChange={(e) => handleChange('meta_title', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  data-testid="input-meta-title"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {settings.meta_title?.length || 0}/60 caractères recommandés
                </p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Description Meta
                </label>
                <textarea
                  value={settings.meta_description}
                  onChange={(e) => handleChange('meta_description', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  data-testid="input-meta-desc"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {settings.meta_description?.length || 0}/160 caractères recommandés
                </p>
              </div>
            </div>
          </div>

          {/* Email Settings (SendGrid) */}
          <div className="bg-white p-6 shadow-sm">
            <h2 
              className="text-xl font-bold text-[#1a1a1a] mb-6 border-b border-gray-100 pb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Paramètres Email (SendGrid)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Clé API SendGrid
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={settings.sendgrid_api_key}
                    onChange={(e) => handleChange('sendgrid_api_key', e.target.value)}
                    className="w-full px-4 py-2 pr-12 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                    placeholder="SG.xxxxx..."
                    data-testid="input-sendgrid-key"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Obtenez votre clé sur sendgrid.com
                </p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Email de notification
                </label>
                <input
                  type="email"
                  value={settings.notification_email}
                  onChange={(e) => handleChange('notification_email', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  placeholder="admin@votresite.com"
                  data-testid="input-notification-email"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Recevez les nouvelles demandes de devis
                </p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <strong>Note :</strong> L'intégration SendGrid sera activée une fois la clé API configurée. 
                Vous recevrez alors automatiquement un email à chaque nouvelle demande de devis.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
